import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';

// ─── Slide data ────────────────────────────────────────────────────────────────
// desktopImg → landscape banner (Ecom_02, Ecom_03) shown on ≥768 px screens
// mobileImg  → portrait banner  (Ecom_05, Ecom_06) shown on <768 px screens
const slides = [
  {
    id: 1,
    desktopImg: '/assets/images/bg/Ecom_02.jpg',
    mobileImg:  '/assets/images/bg/Ecom_05.jpg',
    alt:  'From Farm to Fashion – Everything You Need, Delivered!',
    link: '/shop',
  },
  {
    id: 2,
    desktopImg: '/assets/images/bg/Ecom_03.jpg',
    mobileImg:  '/assets/images/bg/Ecom_06.jpg',
    alt:  "Your Family's Complete Shopping Destination",
    link: '/shop',
  },
];

const AUTOPLAY_MS = 5000;

const BannerOne = () => {
  const [current,  setCurrent]  = useState(0);
  const [animating, setAnimating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  /* ── Detect viewport width ──────────────────────────────────────────────── */
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  /* ── Navigation ─────────────────────────────────────────────────────────── */
  const goTo = useCallback((index) => {
    if (animating) return;
    setAnimating(true);
    setProgress(0);
    setTimeout(() => { setCurrent(index); setAnimating(false); }, 520);
  }, [animating]);

  const next = useCallback(() => goTo((current + 1) % slides.length), [current, goTo]);
  const prev = useCallback(() => goTo((current - 1 + slides.length) % slides.length), [current, goTo]);

  /* ── Auto-play with progress bar ────────────────────────────────────────── */
  useEffect(() => {
    const TICK = 50;
    let elapsed = 0;
    const id = setInterval(() => {
      elapsed += TICK;
      setProgress((elapsed / AUTOPLAY_MS) * 100);
      if (elapsed >= AUTOPLAY_MS) { elapsed = 0; next(); }
    }, TICK);
    return () => clearInterval(id);
  }, [next]);

  /* ── Touch / swipe ──────────────────────────────────────────────────────── */
  const touchStart = React.useRef(null);
  const onTouchStart = (e) => { touchStart.current = e.touches[0].clientX; };
  const onTouchEnd   = (e) => {
    if (touchStart.current === null) return;
    const diff = touchStart.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 40) diff > 0 ? next() : prev();
    touchStart.current = null;
  };

  const slide = slides[current];

  return (
    <>
      <style>{`
        /* ── wrapper ─────────────────────────────────────── */
        .bnr { position: relative; width: 100%; user-select: none; }

        /* ── track / canvas ──────────────────────────────── */
        .bnr__track {
          position: relative;
          width: 98%;
          margin: 0 auto;
          overflow: hidden;
          background: #eee;
          border-radius:20px;
          /* Default: landscape 3:1 ratio  */
          aspect-ratio: 1440 / 480;
        }

        /* ── individual slide ────────────────────────────── */
        .bnr__slide {
          position: absolute;
          inset: 0;
          opacity: 0;
          transform: scale(1.04);
          transition: opacity 0.52s ease, transform 0.52s ease;
          pointer-events: none;
        }
        .bnr__slide.active {
          opacity: 1;
          transform: scale(1);
          pointer-events: auto;
        }
        .bnr__slide img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center top;
          display: block;
        }
        /* invisible click target over the whole slide */
        .bnr__link {
          position: absolute;
          inset: 0;
          z-index: 2;
        }

        /* ── progress bar ────────────────────────────────── */
        .bnr__progress {
          position: absolute;
          bottom: 0; left: 0; right: 0;
          height: 4px;
          background: rgba(0,0,0,0.15);
          z-index: 8;
        }
        .bnr__progress-fill {
          height: 100%;
          background: rgba(255,255,255,0.9);
          transition: width 0.05s linear;
          box-shadow: 0 0 10px rgba(255,255,255,0.7);
        }

        /* ── arrow buttons ───────────────────────────────── */
        .bnr__arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          z-index: 9;
          width: 48px; height: 48px;
          border-radius: 50%;
          border: none;
          cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          font-size: 20px;
          background: rgba(255,255,255,0.88);
          color: #222;
          box-shadow: 0 4px 18px rgba(0,0,0,0.2);
          backdrop-filter: blur(8px);
          transition: background 0.2s, transform 0.2s, opacity 0.25s;
          opacity: 0;
        }
        .bnr:hover .bnr__arrow { opacity: 1; }
        .bnr__arrow:hover      { background: #fff; transform: translateY(-50%) scale(1.1); }
        .bnr__arrow--prev { left: 20px; }
        .bnr__arrow--next { right: 20px; }

        /* ── dot indicators ──────────────────────────────── */
        .bnr__dots {
          position: absolute;
          bottom: 16px; left: 50%;
          transform: translateX(-50%);
          z-index: 9;
          display: flex; gap: 8px; align-items: center;
        }
        .bnr__dot {
          width: 8px; height: 8px;
          border-radius: 100px;
          background: rgba(255,255,255,0.45);
          border: none; cursor: pointer; padding: 0;
          transition: all 0.3s;
        }
        .bnr__dot.active {
          width: 26px;
          background: #fff;
          box-shadow: 0 0 8px rgba(255,255,255,0.55);
        }

        /* ── scroll-down pill ────────────────────────────── */
        .bnr__scroll {
          position: absolute;
          bottom: -22px; left: 50%;
          transform: translateX(-50%);
          z-index: 20;
          width: 46px; height: 46px;
          border-radius: 50%;
          background: #22c55e;
          border: 3px solid #fff;
          display: flex; align-items: center; justify-content: center;
          color: #fff; font-size: 18px;
          text-decoration: none;
          box-shadow: 0 6px 20px rgba(34,197,94,0.4);
          animation: bnrBob 2.5s ease-in-out infinite;
        }
        @keyframes bnrBob {
          0%,100% { bottom: -22px; }
          50%      { bottom: -29px; }
        }

        /* ═══════════════════════════════════════════════════
           RESPONSIVE BREAKPOINTS
        ═══════════════════════════════════════════════════ */

        /* Large desktop */
        @media (min-width: 1400px) {
          .bnr__track { aspect-ratio: 1440 / 480; }
        }

        /* Standard desktop / laptop */
        @media (max-width: 1399px) and (min-width: 1025px) {
          .bnr__track { aspect-ratio: 1280 / 460; }
        }

        /* Tablet landscape */
        @media (max-width: 1024px) and (min-width: 768px) {
          .bnr__track  { aspect-ratio: 1024 / 420; }
          .bnr__arrow  { opacity: 1; width: 40px; height: 40px; font-size: 16px; }
          .bnr__arrow--prev { left: 12px; }
          .bnr__arrow--next { right: 12px; }
        }

        /* ── MOBILE  ──────────────────────────────────────── */
        /* Portrait banners take over on mobile */
        @media (max-width: 767px) {
          .bnr__track {
            aspect-ratio: 3 / 4;   /* portrait ratio matching Ecom_05/06 */
            border-radius: 18px;
            margin: 10px auto;
            width: calc(100% - 20px);
          }
          @media (max-width: 767px) {
          .bnr {
          display: none;
           }
}
          .bnr__slide img {
            object-position: top center;
          }
          .bnr__arrow  { display: none; }         /* swipe instead */
          .bnr__dots   { bottom: 14px; gap: 7px; }
          .bnr__dot    { width: 7px; height: 7px; }
          .bnr__dot.active { width: 20px; }
          .bnr__scroll { width: 40px; height: 40px; font-size: 15px; }
        }

        @media (max-width: 420px) {
          .bnr__track {
            aspect-ratio: 9 / 12;
            border-radius: 14px;
            margin: 8px auto;
            width: calc(100% - 16px);
          }
        }
      `}</style>

      <div className="bnr">
        {/* ── Slide track ─────────────────────────────────────── */}
        <div
          className="bnr__track"
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          {slides.map((s, i) => (
            <div key={s.id} className={`bnr__slide${i === current ? ' active' : ''}`}>
              {/* Switch image source based on viewport */}
              <img
                src={isMobile ? s.mobileImg : s.desktopImg}
                alt={s.alt}
                loading={i === 0 ? 'eager' : 'lazy'}
              />
              <Link to={s.link} className="bnr__link" aria-label={s.alt} />
            </div>
          ))}

          {/* Arrows (visible on hover / always on tablet) */}
          <button className="bnr__arrow bnr__arrow--prev" onClick={prev} aria-label="Previous">
            <i className="ph ph-caret-left" />
          </button>
          <button className="bnr__arrow bnr__arrow--next" onClick={next} aria-label="Next">
            <i className="ph ph-caret-right" />
          </button>

          {/* Dots */}
          <div className="bnr__dots">
            {slides.map((s, i) => (
              <button
                key={s.id}
                className={`bnr__dot${i === current ? ' active' : ''}`}
                onClick={() => goTo(i)}
                aria-label={`Slide ${i + 1}`}
              />
            ))}
          </div>

          {/* Progress bar */}
          <div className="bnr__progress">
            <div className="bnr__progress-fill" style={{ width: `${progress}%` }} />
          </div>
        </div>

        {/* Scroll-down button */}
        <a href="#featureSection" className="bnr__scroll" aria-label="Scroll down">
          <i className="ph ph-caret-double-down" />
        </a>
      </div>
    </>
  );
};

export default BannerOne;